import {
  AreaChart,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { useChartColors } from "@/features/dashboard/components/chartTheme";
import { useChartReveal } from "@/features/dashboard/components/motion";
import type { MessageVolumePoint } from "../mockAnalytics";

interface MessageVolumeChartProps {
  data: MessageVolumePoint[];
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 p-2.5 shadow-xl backdrop-blur-sm text-xs">
      {label && <p className="mb-1.5 font-semibold text-slate-700 dark:text-slate-200">{label}</p>}
      <div className="space-y-1">
        {payload.map((entry) => (
          <div key={entry.name} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-slate-600 dark:text-slate-400 capitalize">{entry.name}</span>
            </div>
            <span className="font-bold tabular-nums text-slate-900 dark:text-white">
              {entry.value?.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Multi-series message volume over time, broken out per messaging platform. */
export function MessageVolumeChart({ data }: MessageVolumeChartProps) {
  const colors = useChartColors();
  const { ref, revealed, isAnimationActive } = useChartReveal();

  const series = [
    { key: "messenger", name: "Messenger", color: colors.channel.messenger },
    { key: "instagram", name: "Instagram", color: colors.channel.instagram },
    { key: "whatsapp", name: "WhatsApp", color: colors.channel.whatsapp },
  ] as const;

  return (
    <div ref={ref} className="h-64 md:h-72">
      {revealed && (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 6, right: 8, left: -4, bottom: 0 }}>
            <defs>
              {series.map((s) => (
                <linearGradient key={s.key} id={`msgVol-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} className="opacity-40 dark:opacity-20" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: colors.mutedForeground }}
              tickLine={false}
              axisLine={false}
              minTickGap={24}
            />
            <YAxis
              tick={{ fontSize: 11, fill: colors.mutedForeground }}
              tickLine={false}
              axisLine={false}
              width={38}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="circle"
              iconSize={8}
              formatter={(value) => <span className="text-xs text-slate-600 dark:text-slate-300">{value}</span>}
            />
            {series.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                fill={`url(#msgVol-${s.key})`}
                stackId="volume"
                isAnimationActive={isAnimationActive}
                animationDuration={900}
                animationEasing="ease-out"
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
